// Painel debug do motor de precisão.
// Render apenas quando ?debug=precision está na URL OU em dev mode.
// Lê auditLogStore + agrega via precisionTelemetryAggregate.

import { useEffect, useState } from "react";
import { useAuditLogStore } from "../stores/auditLogStore";
import { aggregatePrecisionTelemetry } from "../utils/precisionTelemetryAggregate";
import { isFeatureEnabled, setFeatureFlag } from "../utils/featureFlags";

function pct(n) {
  if (!Number.isFinite(n)) return "—";
  return (n * 100).toFixed(1) + "%";
}
function ms(n) {
  if (!Number.isFinite(n) || n === null) return "—";
  return n + "ms";
}

export function PrecisionDebugPanel() {
  const log = useAuditLogStore(s => s.log);
  const [agg, setAgg] = useState(() => aggregatePrecisionTelemetry(log));
  const [escalationFlag, setEscalationFlag] = useState(() => isFeatureEnabled("precisionAutoEscalation"));
  const [telemetryFlag, setTelemetryFlag] = useState(() => isFeatureEnabled("aiTelemetryEnabled"));
  const [guideFlag, setGuideFlag] = useState(() => isFeatureEnabled("precisionGuideMode"));

  useEffect(() => {
    setAgg(aggregatePrecisionTelemetry(log));
  }, [log]);

  const toggleEscalation = () => {
    const next = !escalationFlag;
    setFeatureFlag("precisionAutoEscalation", next);
    setEscalationFlag(next);
  };
  const toggleTelemetry = () => {
    const next = !telemetryFlag;
    setFeatureFlag("aiTelemetryEnabled", next);
    setTelemetryFlag(next);
  };
  const toggleGuide = () => {
    const next = !guideFlag;
    setFeatureFlag("precisionGuideMode", next);
    setGuideFlag(next);
  };

  const sigs = Object.entries(agg.signalFrequency).sort((a, b) => b[1] - a[1]);
  const mods = Object.entries(agg.byModule).sort((a, b) => b[1].runs - a[1].runs);
  const models = Object.entries(agg.byModel).sort((a, b) => b[1] - a[1]);

  return (
    <div className="min-h-screen bg-background text-foreground p-6 font-mono text-[12px]">
      <div className="max-w-4xl mx-auto space-y-6">
        <header className="border-b border-border pb-3">
          <h1 className="text-[16px] font-bold tracking-wider uppercase">Precision Debug</h1>
          <p className="text-muted-foreground text-[11px] mt-1">
            telemetria do motor hybrid Sonnet 4.6 → Opus 4.7 · agregado de {agg.totalRuns} run{agg.totalRuns === 1 ? "" : "s"}
          </p>
        </header>

        <section>
          <h2 className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Flags</h2>
          <div className="flex gap-3">
            <button
              onClick={toggleEscalation}
              className={`px-3 py-1.5 rounded border text-[11px] ${escalationFlag ? "bg-primary/15 border-primary text-primary" : "border-border text-muted-foreground"}`}
            >
              precisionAutoEscalation: {escalationFlag ? "ON" : "OFF"}
            </button>
            <button
              onClick={toggleTelemetry}
              className={`px-3 py-1.5 rounded border text-[11px] ${telemetryFlag ? "bg-primary/15 border-primary text-primary" : "border-border text-muted-foreground"}`}
            >
              aiTelemetryEnabled: {telemetryFlag ? "ON" : "OFF"}
            </button>
            <button
              onClick={toggleGuide}
              className={`px-3 py-1.5 rounded border text-[11px] ${guideFlag ? "bg-primary/15 border-primary text-primary" : "border-border text-muted-foreground"}`}
            >
              precisionGuideMode: {guideFlag ? "GUIA" : "ANÁLISE"}
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
            modo guia: pós-processador reduz output em confiança baixa (postura norteador). modo análise: output completo.
          </p>
        </section>

        <section>
          <h2 className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Métricas globais</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric label="Runs" value={agg.totalRuns} />
            <Metric label="Escalados" value={`${agg.escalatedCount} (${pct(agg.escalationRate)})`} />
            <Metric label="Latência avg" value={ms(agg.avgLatencyMs)} />
            <Metric label="Lat. esc / non" value={`${ms(agg.avgLatencyEscalatedMs)} / ${ms(agg.avgLatencyNonEscalatedMs)}`} />
            <Metric label="Truncados" value={`${agg.truncatedCount} (${pct(agg.truncationRate)})`} />
            <Metric label="Output avg (tok)" value={agg.avgOutputTokens ?? "—"} />
            <Metric label="Context chars avg" value={agg.avgContextChars ?? "—"} />
            <Metric label="Cache hit rate" value={agg.cacheHitRate != null ? pct(agg.cacheHitRate) : "—"} />
          </div>
        </section>

        <section>
          <h2 className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Sinais (frequência)</h2>
          {sigs.length === 0 ? (
            <p className="text-muted-foreground text-[11px]">Nenhum sinal disparado.</p>
          ) : (
            <ul className="space-y-1">
              {sigs.map(([k, v]) => (
                <li key={k} className="flex justify-between border-b border-border/50 py-1">
                  <span>{k}</span>
                  <span className="text-primary">{v}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h2 className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Por módulo</h2>
          {mods.length === 0 ? (
            <p className="text-muted-foreground text-[11px]">Sem dados.</p>
          ) : (
            <table className="w-full text-[11px]">
              <thead className="text-muted-foreground">
                <tr><th className="text-left py-1">módulo</th><th className="text-right">runs</th><th className="text-right">escalados</th><th className="text-right">rate</th></tr>
              </thead>
              <tbody>
                {mods.map(([k, v]) => (
                  <tr key={k} className="border-b border-border/50">
                    <td className="py-1">{k}</td>
                    <td className="text-right">{v.runs}</td>
                    <td className="text-right">{v.escalated}</td>
                    <td className="text-right text-primary">{pct(v.rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section>
          <h2 className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Por modelo</h2>
          {models.length === 0 ? (
            <p className="text-muted-foreground text-[11px]">Sem dados.</p>
          ) : (
            <ul className="space-y-1">
              {models.map(([k, v]) => (
                <li key={k} className="flex justify-between border-b border-border/50 py-1">
                  <span>{k}</span>
                  <span className="text-primary">{v}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <footer className="text-muted-foreground text-[10px] pt-4 border-t border-border">
          Console: __norm.precisionStats() · __norm.precisionRaw() · __norm.precisionLog()
        </footer>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="border border-border rounded p-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-[14px] font-bold mt-1">{value}</div>
    </div>
  );
}
